import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { StorageFindInterface } from '@zaoblako/shared/modules/storage/dtos/storage/storage-find.dto';
import { StorageFindManyQueryInterface } from '@zaoblako/shared/modules/storage/dtos/storage/storage-find-many-query.dto';
import { StorageService } from '@zaoblako/web/core/services/storage/storage.service';
import { GlobalService } from '@zaoblako/web/core/services/global.service';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';

@Component({
  selector: 'crm-storage-single-select',
  standalone: true,
  imports: [CommonModule, SingleSelectComponent, SpinnerComponent],
  templateUrl: './storage-single-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class StorageSingleSelectComponent implements OnInit {
  @ViewChild('singleSelectComponent', { static: true }) public singleSelectComponent: SingleSelectComponent;

  @Input() public storages: StorageFindInterface[] = [];
  public queryParams: StorageFindManyQueryInterface = {
    limit: 40,
    offset: 0
  };

  public loading = false;
  public paginationLoading = false;
  public count = 0;

  public _selectedStorage: StorageFindInterface;
  @Input() public set selectedStorage(selectedStorage: StorageFindInterface) {
    if (this.customSearch) {
      this._selectedStorage = selectedStorage;
      return;
    }

    if (this.storages.some((storage) => storage.id === selectedStorage?.id)) {
      this._selectedStorage = this.storages.find((storage) => storage.id === selectedStorage.id);
      return;
    }

    if (selectedStorage?.id) {
      this.storageService.findMany({ includeIds: [selectedStorage.id], limit: 1, offset: 0 }).subscribe({
        next: (data) => {
          if (data.items[0]) {
            const _storage = data.items[0];
            if (!this.storages.some((storage) => storage.id === _storage.id)) {
              this.storages = this.storages.concat(_storage);
              this.cdRef.markForCheck();
            }
            setTimeout(() => {
              this._selectedStorage = _storage;
              this.cdRef.markForCheck();
            }, 30);
          }
        }
      });
    }
  }

  @Output() public selectedStorageChange = new EventEmitter<StorageFindInterface>();

  @Input() public isError = false;

  @Input() public class: string;
  @Input() public styleClass: string;
  @Input() public disabled: boolean;
  @Input() public appendTo: string;
  @Input() public showClear = true;
  @Input() public labelInput = false;

  @Input() public customSearch = false;

  @Input() public set setSelectedStorageId(id: string) {
    if (id === null || id === undefined) {
      return;
    }

    if (this.storages.some((storage) => storage.id === id)) {
      this._selectedStorage = this.storages.find((storage) => storage.id === id);
      return;
    }

    this.storageService.findMany({ includeIds: [id], limit: 1, offset: 0 }).subscribe({
      next: (data) => {
        if (data.items[0]) {
          const _storage = data.items[0];
          if (!this.storages.some((storage) => storage.id === _storage.id)) {
            this.storages = this.storages.concat(_storage);
            this.cdRef.markForCheck();
          }
          setTimeout(() => {
            this._selectedStorage = _storage;
            this.cdRef.markForCheck();
          }, 30);
        }
      }
    });
  }

  public constructor(
    private readonly storageService: StorageService,
    private readonly globalService: GlobalService,
    private readonly cdRef: ChangeDetectorRef
  ) {}

  public ngOnInit(): void {
    if (!this.customSearch) {
      this.loading = true;
      this.cdRef.markForCheck();

      this.storageService.findMany(this.queryParams).subscribe({
        next: (data) => {
          this.storages = this.storages?.length
            ? this.storages.concat(
                data.items.filter((item) => !this.storages.some((storage) => storage.id === item.id))
              )
            : data.items.filter((item) => !this.storages.some((storage) => storage.id === item.id));
          this.count = data.count;
          this.loading = false;
          this.cdRef.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.cdRef.markForCheck();
        }
      });
    }
  }

  public onSelect(storage: StorageFindInterface) {
    this.selectedStorageChange.emit(storage);
  }

  public onShowPanel() {
    if (!this.customSearch) {
      setTimeout(() => {
        this.singleSelectComponent?.dropdown?.scroller.onScroll.subscribe(() => {
          const el: HTMLElement = this.singleSelectComponent?.dropdown?.scroller?.elementViewChild?.nativeElement;
          if (el.offsetHeight + Math.ceil(el.scrollTop) >= el.scrollHeight) {
            if (this.storages.length < this.count && !this.loading && !this.paginationLoading) {
              this.paginationLoading = true;
              this.queryParams.offset += this.queryParams.limit;
              this.cdRef.markForCheck();

              this.storageService.findMany(this.queryParams).subscribe({
                next: (data) => {
                  this.storages = this.storages.concat(
                    data.items.filter((item) => !this.storages.some((storage) => storage.id === item.id))
                  );
                  this.count = data.count;
                  this.paginationLoading = false;
                  this.cdRef.markForCheck();
                },
                error: () => {
                  this.paginationLoading = false;
                  this.cdRef.markForCheck();
                }
              });
            }
          }
        });
      }, 100);
    }
  }

  public search(name: string) {
    if (!this.customSearch) {
      this.loading = true;
      this.queryParams.search = name;
      this.queryParams.offset = 0;
      this.cdRef.markForCheck();

      this.storageService.findMany(this.queryParams).subscribe({
        next: (data) => {
          if (!data.items.some((item) => item.id === this._selectedStorage?.id) && this._selectedStorage) {
            this.storages = data.items.concat(this._selectedStorage);
          } else {
            this.storages = data.items;
          }
          this.count = data.count;
          this.loading = false;
          this.cdRef.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.cdRef.markForCheck();
        }
      });
    }
  }
}
